import { DEFAULT_LANGUAGE, Language, getBasePathWithoutLanguage } from 'src/utils/i18n-config';

interface PageMetadata {
  title: string;
  description: string;
}

interface MetadataCollection {
  [pageKey: string]: PageMetadata;
}

/**
 * Convert pathname to metadata key
 * Examples:
 *   "/" -> "home"
 *   "/pricing" -> "pricing"
 *   "/es/pricing" -> "pricing"
 *   "/product/ai" -> "product.ai"
 *   "/es/product/ai" -> "product.ai"
 */
function pathnameToKey(pathname: string): string {
  const basePath = getBasePathWithoutLanguage(pathname);

  if (basePath === '/') {
    return 'home';
  }

  // Remove leading slash and convert to dot notation
  return basePath.slice(1).replace(/\//g, '.');
}

/**
 * Load metadata for a specific language
 * Uses synchronous require since this runs at build time
 */
function loadMetadata(language: Language): MetadataCollection {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const metadata = require(`../locales/${language}/metadata.json`) as MetadataCollection;
    return metadata;
  } catch (error) {
    console.warn(`Failed to load metadata for language: ${language}`, error);
    return {};
  }
}

/**
 * Hook to get page metadata (title and description) for the current page
 *
 * This hook runs at build time during Gatsby's Head export, so it uses
 * synchronous require() calls. It accepts the language from pageContext
 * and the pathname from location.
 *
 * @param language - The language code from pageContext (e.g., "en", "es")
 * @param pathname - The pathname from location (e.g., "/pricing", "/es/pricing")
 * @returns Page metadata object with title and description
 *
 * @example
 * ```tsx
 * export const Head = (props: HeadProps): JSX.Element => {
 *   const { title, description } = usePageMetadata(
 *     props.pageContext.language,
 *     props.location.pathname
 *   );
 *
 *   return (
 *     <HeadComponent
 *       helmet={{ title, description }}
 *       location={props.location}
 *     />
 *   );
 * };
 * ```
 */
export const usePageMetadata = (language: Language = DEFAULT_LANGUAGE, pathname = '/'): PageMetadata => {
  // Convert pathname to metadata key
  const pageKey = pathnameToKey(pathname);

  // Load metadata for the language
  const metadata = loadMetadata(language);

  // Get metadata for this specific page
  const pageMetadata = metadata[pageKey];

  if (!pageMetadata) {
    throw new Error(`No metadata found for page "${pageKey}" in language "${language}". ` + `Pathname: ${pathname}`);
  }

  return pageMetadata;
};

export default usePageMetadata;
